/**
 * useErrorHandler.ts
 * Hook pour utiliser ErrorNotificationService dans les composants React
 * Phase 2.5 : Amélioration de la gestion d'erreurs
 */
import { useCallback, useEffect, useState } from 'react';
import {
  ErrorNotificationService,
  type ErrorNotification,
  type ErrorSeverity,
  type ErrorCategory,
  type ErrorFallback,
} from '../services/ErrorNotificationService';
import { useToast } from '../components/react/common/ToastProvider';

/**
 * Hook pour gérer les erreurs dans les composants React
 */
export function useErrorHandler() {
  const [errorService] = useState(() => ErrorNotificationService.getInstance());
  const { addToast } = useToast();
  const [notifications, setNotifications] = useState<ErrorNotification[]>([]);

  useEffect(() => {
    // Configurer le callback pour les toasts
    errorService.setToastCallback(notification => {
      const toastType =
        notification.severity === 'critical' || notification.severity === 'high'
          ? 'error'
          : notification.severity === 'medium'
            ? 'warning'
            : 'info';

      addToast({
        type: toastType,
        message: notification.message,
        duration:
          notification.severity === 'critical'
            ? 10000
            : notification.severity === 'high'
              ? 8000
              : 5000,
        action:
          notification.retryable && notification.retryAction
            ? {
                label: 'Réessayer',
                action: async () => {
                  try {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    await notification.retryAction!();
                  } catch (error) {
                    errorService.notifyError(error, {
                      severity: 'high',
                      context: { retryFailed: true },
                    });
                  }
                },
              }
            : undefined,
      });

      // Mettre à jour la liste des notifications
      setNotifications(errorService.getNotifications(10));
    });

    // Charger les notifications existantes
    setNotifications(errorService.getNotifications(10));
  }, [errorService, addToast]);

  /**
   * Notifie une erreur
   */
  const notifyError = useCallback(
    (
      error: Error | unknown,
      options?: {
        severity?: ErrorSeverity;
        category?: ErrorCategory;
        context?: Record<string, unknown>;
        retryable?: boolean;
        retryAction?: () => Promise<void>;
        silent?: boolean;
      }
    ) => {
      errorService.notifyError(error, options);
    },
    [errorService]
  );

  /**
   * Gère une opération asynchrone avec gestion d'erreur automatique
   */
  const handleAsync = useCallback(
    async <T>(
      operation: () => Promise<T>,
      options?: {
        errorMessage?: string;
        severity?: ErrorSeverity;
        category?: ErrorCategory;
        fallback?: ErrorFallback;
        silent?: boolean;
      }
    ): Promise<T | null> => {
      return errorService.handleAsync(operation, options);
    },
    [errorService]
  );

  /**
   * Gère une opération avec retry automatique
   */
  const handleWithRetry = useCallback(
    async <T>(
      operation: () => Promise<T>,
      options?: {
        maxRetries?: number;
        retryDelay?: number;
        errorMessage?: string;
        severity?: ErrorSeverity;
      }
    ): Promise<T | null> => {
      return errorService.handleWithRetry(operation, options);
    },
    [errorService]
  );

  /**
   * Obtient les notifications récentes
   */
  const getNotifications = useCallback(
    (limit?: number) => {
      return errorService.getNotifications(limit);
    },
    [errorService]
  );

  /**
   * Efface les notifications
   */
  const clearNotifications = useCallback(() => {
    errorService.clearNotifications();
    setNotifications([]);
  }, [errorService]);

  return {
    notifyError,
    handleAsync,
    handleWithRetry,
    notifications,
    getNotifications,
    clearNotifications,
  };
}
